import { useState } from "react";

import { DateHelper } from "@/Common/Helpers/DateHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { Guid } from "@/Common/Models/Guid";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { ResultsDurationType } from "@/Flowsheets/Models/ResultsDurationType";
import { SaveFlowsheetFilterSettingsRequest } from "../Api/Contracts/SaveFlowsheetFilterSettingsRequest";
import { FlowsheetFilterSettingsClient } from "../Api/FlowsheetFilterSettingsClient";
import { CareComponentFilterSetting } from "../Models/CareComponentFilterSetting";
import { CareElementFilterSetting } from "../Models/CareElementFilterSetting";
import { FlowsheetFilterSettings } from "../Models/FlowsheetFilterSettings";

interface IUseFlowsheetFilterSettingsResult {
    saveSettings: boolean;
    setSaveSettings: (value: boolean) => void;
    filterSettings: FlowsheetFilterSettings;
    handleCheckboxChange: (id: keyof Pick<FlowsheetFilterSettings, "OutsideTreatmentTarget" | "OutsideTreatmentFrequency">, checked: boolean, componentName?: string) => void;
    handleCareElementCheckboxChange: (componentName: string, id: string, checked: boolean) => void;
    setResultsDuration: (duration: ResultsDurationType, componentName?: string) => void;
    handleStartDateChange: (value: DateTimeWithPrecision | null, encounterDate: Date, componentName?: string) => void;
    handleEndDateChange: (value: DateTimeWithPrecision | null, encounterDate: Date, componentName?: string) => void;
    handleConfirm: () => Promise<void>;
    isStartDateInvalid: boolean;
    isEndDateInvalid: boolean;
}

/**
 * Custom hook to manage filter settings for a flowsheet.
 * @returns An object containing the filter settings for the flowsheet, including checkboxes, date range, and handlers for updating the settings.
 */
function useFlowsheetFilterSettings(
    filterValues?: FlowsheetFilterSettings,
    flowsheetId?: Guid,
    onConfirmCallback?: (updatedSettings?: FlowsheetFilterSettings, isMainFilter?: boolean) => void
): IUseFlowsheetFilterSettingsResult {
    const [isStartDateInvalid, setIsStartDateInvalid] = useState(false);
    const [isEndDateInvalid, setIsEndDateInvalid] = useState(false);

    const [saveSettings, setSaveSettings] = useState<boolean>(false);

    const [filterSettings, setFilterSettings] = useState<FlowsheetFilterSettings>(
        new FlowsheetFilterSettings({
            OutsideTreatmentTarget: filterValues?.OutsideTreatmentTarget ?? false,
            OutsideTreatmentFrequency: filterValues?.OutsideTreatmentFrequency ?? false,
            ResultsDurationType: filterValues?.ResultsDurationType ?? ResultsDurationType.All,
            StartDate: filterValues?.StartDate ?? "",
            EndDate: filterValues?.EndDate ?? "",
            CareComponentFilterSettings: filterValues?.CareComponentFilterSettings?.map(c => new CareComponentFilterSetting({
                Name: c.Name,
                IsEnabled: c.IsEnabled,
                CareElements: c.CareElements.map(e => new CareElementFilterSetting(e)),
                OutsideTreatmentFrequency: c.OutsideTreatmentFrequency,
                OutsideTreatmentTarget: c.OutsideTreatmentTarget,
                ResultsDurationType: c.ResultsDurationType,
                StartDate: c.StartDate,
                EndDate: c.EndDate
            })) ?? []
        })
    );

    function updateFilterSetting<
        T extends keyof CareComponentFilterSetting & keyof FlowsheetFilterSettings
    >(
        prev: FlowsheetFilterSettings,
        field: T,
        value: CareComponentFilterSetting[T],
        componentName?: string
    ): FlowsheetFilterSettings {
        const updateComponent = (component: CareComponentFilterSetting): CareComponentFilterSetting =>
            componentName && component.Name !== componentName
                ? component
                : new CareComponentFilterSetting({ ...component, [field]: value });

        const updatedComponents = prev.CareComponentFilterSettings?.map(updateComponent) ?? [];

        return new FlowsheetFilterSettings({
            ...prev,
            ...(componentName ? {} : { [field]: value }),
            CareComponentFilterSettings: updatedComponents
        });
    }

    const handleDateChange = (
        field: "StartDate" | "EndDate",
        value: DateTimeWithPrecision | null,
        componentName?: string
    ): void => {
        const newDate = value?.status === LifeStageDateStatusEnum.Valid && value.isDayPrecision
            ? DateHelper.formatDateLuxon(value.value, "yyyy-MM-dd")
            : "";

        setFilterSettings(prev => updateFilterSetting(prev, field, newDate, componentName));
    };

    const handleCheckboxChange = (
        id: keyof Pick<FlowsheetFilterSettings, "OutsideTreatmentTarget" | "OutsideTreatmentFrequency">,
        checked: boolean,
        componentName?: string
    ): void => {
        setFilterSettings(prev => updateFilterSetting(prev, id, checked, componentName));
    };

    const handleCareElementCheckboxChange = (componentName: string, id: string, checked: boolean): void => {
        setFilterSettings(prev => {
            const updatedComponents = prev.CareComponentFilterSettings?.map(component => {
                if (component.Name !== componentName) return component;
                const updatedElements = component.CareElements.map(el =>
                    el.Key === id ? new CareElementFilterSetting({ ...el, IsEnabled: checked }) : el
                );
                return new CareComponentFilterSetting({
                    ...component,
                    CareElements: updatedElements
                });
            }) ?? [];
            return new FlowsheetFilterSettings({ ...prev, CareComponentFilterSettings: updatedComponents });
        });
    };

    const handleStartDateChange = (value: DateTimeWithPrecision | null, encounterDate: Date, componentName?: string): void => {
        setIsStartDateInvalid(
            LuxonDateTimeParser.parse(value.value) > LuxonDateTimeParser.parse(encounterDate)
        );
        handleDateChange("StartDate", value, componentName);
    };

    const handleEndDateChange = (value: DateTimeWithPrecision | null, encounterDate: Date, componentName?: string): void => {
        setIsEndDateInvalid(
            LuxonDateTimeParser.parse(value.value) > LuxonDateTimeParser.parse(encounterDate)
        );

        handleDateChange("EndDate", value, componentName);
    };

    const setResultsDuration = (duration: ResultsDurationType, componentName?: string): void => {
        setFilterSettings(prev => updateFilterSetting(prev, "ResultsDurationType", duration, componentName));
    };

    const handleConfirm = async (): Promise<void> => {
        const isMainFilter = filterSettings.CareComponentFilterSettings.some(c => c.IsEnabled);

        if (!saveSettings || !flowsheetId) {
            onConfirmCallback?.(filterSettings, isMainFilter);
            return;
        }

        const request: SaveFlowsheetFilterSettingsRequest = {
            FlowsheetId: flowsheetId,
            FilterSettings: filterSettings
        };

        const response = await FlowsheetFilterSettingsClient.saveFlowsheetSettings(request);
        response.throwIfError();

        onConfirmCallback?.(filterSettings, isMainFilter);
    };

    return {
        saveSettings,
        setSaveSettings,
        filterSettings,
        handleCheckboxChange,
        handleCareElementCheckboxChange,
        setResultsDuration,
        handleStartDateChange,
        handleEndDateChange,
        handleConfirm,
        isStartDateInvalid,
        isEndDateInvalid
    };
}

export { useFlowsheetFilterSettings };
